import FileName from "../FileName";
import { v4 as uuid } from "uuid";
import { useEffect, useMemo, useRef, useState } from "react";
import Spinner from "../../../../../Spinner";
import filterResponse from "../../../../../../filterResponse";
import PostalMime from "postal-mime";
import { RTFJS } from "rtf.js";
import { stringToArrayBuffer } from "../../../../../../util/functions";

const allowedPreviewExtensions = ['pdf', 'txt', 'json', 'csv', 'tsv', 'eml', 'html'];

export default function PreviewTextFile({ file, highlight }) {
    const id = useMemo(() => uuid(), [file.key]);
    const [loading, setLoading] = useState(false);
    const modelRef = useRef();

    const docRef = useRef();

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();
        const modelReference = modelRef.current;

        async function onShown() {
            if (!docRef.current.src && !docRef.current.srcdoc) {
                try {
                    setLoading(true);

                    if (file.key.endsWith('.eml')) {
                        const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + file.key, {
                            signal: abortController.signal,
                            credentials: "include"
                        });
                        await filterResponse(response);
                        const blob = await response.text();
                        try {
                            const mail = await PostalMime.parse(blob);
                            if (isMounted) {
                                if (mail.html) {
                                    docRef.current.srcdoc = mail.html;
                                }
                                else if (mail.text) {
                                    const escaped = mail.text
                                        .replace(/&/g, "&amp;")
                                        .replace(/</g, "&lt;")
                                        .replace(/>/g, "&gt;");
                                    docRef.current.srcdoc = `<pre style="white-space: pre-wrap; font-family: sans-serif;">${escaped}</pre>`;
                                }
                            }
                        }
                        catch (e) {
                            console.error(e);
                        }
                    }
                    else if (file.key.endsWith('.rtf')) {
                        const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + file.key, {
                            credentials: "include",
                            signal: abortController.signal
                        });
                        await filterResponse(response);
                        const blob = await response.text();
                        const rtfDoc = new RTFJS.Document(stringToArrayBuffer(blob));
                        const html = await rtfDoc.render();

                        const doc = document.createElement("html");
                        const body = document.createElement("body");
                        for (const element of html) {
                            body.append(element);
                        }
                        doc.append(body);

                        isMounted && (docRef.current.srcdoc = doc.outerHTML);
                    }
                    else {
                        isMounted && (docRef.current.src = process.env.REACT_APP_ENDPOINT + '/files/' + file.key);
                    }

                }
                catch (e) {
                    console.error(e);
                }
                finally {
                    isMounted && setLoading(false);
                }
            }
        }

        if (modelReference) {
            modelReference.addEventListener("shown.bs.modal", onShown);
        }

        return () => {
            isMounted = false;
            abortController.abort();
            if (modelReference) {
                modelReference.removeEventListener("shown.bs.modal", onShown);
            }
        };
    }, [file.key, id]);

    const sandbox = useMemo(() => {
        if (file.key.endsWith('.html')) {
            return "allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-forms"
        }
        return null;
    }, [file.key]);

    return (
        <>
            <a href={"#"} data-bs-toggle="modal" data-bs-target={"#previewFileModal" + id}>
                <FileName name={file.fileName} highlight={highlight} />
            </a>

            <div ref={modelRef} className="modal fade" id={"previewFileModal" + id} tabIndex="-1" aria-labelledby={"previewFileModalLabel" + id} aria-hidden="true">
                <div className="modal-dialog modal-fullscreen">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id={"previewFileModalLabel" + id}>Preview {file.fileName}</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className={"text-center h-100"}>
                                {loading && <Spinner />}
                                <iframe {...(sandbox && { sandbox: sandbox })} width={'100%'} height={'100%'} ref={docRef} ></iframe>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
